import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Filter } from 'lucide-react';
import { ProfessionalActiveFilterChips } from './ProfessionalActiveFilterChips';
import { ProfessionalCard } from './ProfessionalCard';
import { Skeleton } from '@/components/ui/skeleton';
import { ExtendedProfessionalEntry, ProfessionalFilters } from '@/types/professional';

interface ProfessionalResultsPaneProps {
  results: ExtendedProfessionalEntry[];
  loading: boolean;
  totalCount: number;
  filters: ProfessionalFilters;
  sortBy: string;
  onSortChange: (value: string) => void;
  onFilterRemove: (key: string, value: string) => void;
  onClearAllFilters: () => void;
  onShowMobileFilters: () => void;
}

export function ProfessionalResultsPane({
  results,
  loading,
  totalCount,
  filters,
  sortBy,
  onSortChange,
  onFilterRemove,
  onClearAllFilters,
  onShowMobileFilters
}: ProfessionalResultsPaneProps) {
  return (
    <div className="space-y-6">
      {/* Utility Bar */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-4">
        <div className="flex items-center gap-4">
          {/* Mobile Filters Button */}
          <Button 
            variant="outline" 
            onClick={onShowMobileFilters}
            className="lg:hidden gap-2"
          >
            <Filter className="w-4 h-4" />
            Filters
          </Button>
          
          {/* Results Count */}
          <p className="text-sm text-st_taupe" aria-live="polite">
            {loading ? 'Searching...' : `${totalCount} professionals found`}
          </p>
        </div>

        {/* Sort */}
        <div className="flex items-center gap-2">
          <span className="text-sm text-st_taupe">Sort by:</span>
          <Select value={sortBy} onValueChange={onSortChange}>
            <SelectTrigger className="w-48">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="best_match">Best match</SelectItem>
              <SelectItem value="experience_high_low">Most experience</SelectItem>
              <SelectItem value="experience_low_high">Least experience</SelectItem>
              <SelectItem value="location">Closest first</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Active Filter Chips */}
      <ProfessionalActiveFilterChips
        filters={filters}
        onFilterRemove={onFilterRemove}
        onClearAll={onClearAllFilters}
      />

      {/* Results Grid */}
      {loading ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-6">
          {Array.from({ length: 6 }).map((_, i) => (
            <div key={i} className="space-y-4 p-6 border rounded-2xl">
              <div className="flex items-center space-x-4">
                <Skeleton className="h-16 w-16 rounded-full" />
                <div className="space-y-2">
                  <Skeleton className="h-4 w-[150px]" />
                  <Skeleton className="h-3 w-[100px]" />
                </div>
              </div>
              <div className="space-y-2">
                <Skeleton className="h-4 w-full" />
                <Skeleton className="h-4 w-full" />
                <Skeleton className="h-4 w-[80%]" />
              </div>
            </div>
          ))}
        </div>
      ) : results.length > 0 ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-6">
          {results.map((professional) => (
            <ProfessionalCard key={professional.id} professional={professional} />
          ))}
        </div>
      ) : (
        <div className="text-center py-12">
          <div className="max-w-md mx-auto">
            <h3 className="text-lg font-semibold text-foreground mb-2">
              No professionals found
            </h3>
            <p className="text-muted-foreground mb-6">
              Try adjusting your filters or expanding your search area to find professionals in your region.
            </p>
            <div className="space-y-3">
              <Button onClick={onClearAllFilters} variant="outline" className="w-full">
                Clear all filters
              </Button>
              <Button asChild className="w-full bg-st_light_blue hover:bg-st_light_blue/90">
                <a href="/professionals/join">Join Our Network</a>
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}